import React, { useState, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { FormField } from '@xingternal/text-field';
import { Button, TextButton } from '@xingternal/button';
import { trackEditSaving } from '@xingternal/pages-tracking';

import {
  useDialogContext,
  NotSavedDialog,
  usePageContext,
  useEditContext,
  ENTITY_PAGE_QUERY,
  getEntityPageQueryVariables,
  EditHeader,
} from '@xingternal/pages-common';
import type { EntityPageQuery } from '@xingternal/pages-common';
import { ErrorContext } from '@xingternal/pages-page-error';

import { useEntityPageUpdateConfigurationMutation } from './UpdatePageInfo.gql-types';

import styles from './PageInfo.css';

const MAX_TITLE_CHARS = 80;
const MAX_SLOGAN_CHARS = 64;

const PageInfo = () => {
  const { executeWithDialog } = useDialogContext();
  const { pageContext, setPageContext } = usePageContext();
  const [slogan, setSlogan] = useState(pageContext?.pageSlogan || '');
  const [title, setTitle] = useState(pageContext?.pageTitle || '');
  const intl = useIntl();

  const { isAdmin, trackingData } = useEditContext();
  const displayBanner = pageContext?.focusType === 'COMPANY';
  const userHasPermissionsToEditTitle = isAdmin;

  const pageSlug = pageContext?.pageSlug;

  const { dataChanged, setDataChanged, dialogConfirmation } =
    useDialogContext();

  const { showError } = useContext(ErrorContext);

  const handleTitleChange = (newTitle: string) => {
    if (newTitle.length <= MAX_TITLE_CHARS) {
      setDataChanged(true);
      setTitle(newTitle);
    }
  };

  const handleSloganChange = (newSlogan: string) => {
    if (newSlogan.length <= MAX_SLOGAN_CHARS) {
      setDataChanged(true);
      setSlogan(newSlogan);
    }
  };

  const [updatePageInfo, { loading }] =
    useEntityPageUpdateConfigurationMutation({
      onCompleted: (data) => {
        if (data?.entityPageUpdateConfiguration?.error) {
          showError({
            message: 'EP_JOBS_FEEDBACK_ERROR',
            error: data.entityPageUpdateConfiguration.error,
          });
        } else {
          setDataChanged(false);
          pageContext.goBackUrl && pageContext.goBackUrl();
        }
      },
      onError: (error) =>
        showError({
          message: 'EP_JOBS_FEEDBACK_ERROR',
          error: error,
        }),
      update: (cache) => {
        const data = cache.readQuery<EntityPageQuery>({
          query: ENTITY_PAGE_QUERY,
          variables: getEntityPageQueryVariables(pageContext.pageSlug),
        });

        cache.writeQuery({
          query: ENTITY_PAGE_QUERY,
          variables: getEntityPageQueryVariables(pageContext.pageSlug),
          data: {
            entityPageEX: {
              ...(data?.entityPageEX || {}),
              title,
              slogan,
            },
          },
        });

        setPageContext({
          ...pageContext,
          pageSlogan: slogan,
          pageTitle: title,
        });
      },
    });

  const handleMutation = () => {
    if (!dataChanged) {
      pageContext.goBackUrl && pageContext.goBackUrl();
      return;
    }
    // remove extra spaces between words, end and beggining
    const mutationTitle = title.replace(/^\s+|\s+$/g, '');
    const mutationSlogan = slogan.replace(/^\s+|\s+$/g, '');

    updatePageInfo({
      variables: {
        pageId: pageSlug,
        slogan: mutationSlogan.length > 0 ? mutationSlogan : null,
        ...(userHasPermissionsToEditTitle && {
          title: mutationTitle.length > 0 ? mutationTitle : null,
        }),
      },
    });
  };

  return (
    <>
      <EditHeader
        titleKey="EP_SLOGAN_EDIT_HEADLINE"
        userGuidanceCopy="EP_SLOGAN_EDIT_HEADLINE_INFO"
      />
      {userHasPermissionsToEditTitle && (
        <div className={styles.formFieldWrapper} data-cy="EDIT_PAGE_INFO_TITLE">
          {!displayBanner && (
            <FormField
              id="EDIT_PAGE_INFO_TITLE_INPUT"
              medium
              value={title}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                handleTitleChange(e.target.value)
              }
              label={intl.formatMessage({
                id: 'EP_TITLE_EDIT_LABEL',
                defaultMessage: 'EP_TITLE_EDIT_LABEL',
              })}
              helperText={
                title && title.length > 0
                  ? intl.formatMessage(
                      {
                        id: 'EP_INPUT_CHARS_LEFT',
                        defaultMessage: 'EP_INPUT_CHARS_LEFT',
                      },
                      { charactersNumber: MAX_TITLE_CHARS - title.length }
                    )
                  : intl.formatMessage({
                      id: 'EP_TITLE_EDIT_HINT',
                      defaultMessage: 'EP_TITLE_EDIT_HINT',
                    })
              }
            />
          )}
        </div>
      )}
      <div className={styles.formFieldWrapper} data-cy="EDIT_PAGE_INFO_SLOGAN">
        <FormField
          id="EDIT_PAGE_INFO_SLOGAN_INPUT"
          medium
          value={slogan}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            handleSloganChange(e.target.value)
          }
          label={intl.formatMessage({
            id: 'EP_SLOGAN_EDIT_LABEL',
            defaultMessage: 'EP_SLOGAN_EDIT_LABEL',
          })}
          helperText={
            slogan && slogan.length > 0
              ? intl.formatMessage(
                  {
                    id: 'EP_INPUT_CHARS_LEFT',
                    defaultMessage: 'EP_INPUT_CHARS_LEFT',
                  },
                  { charactersNumber: MAX_SLOGAN_CHARS - slogan.length }
                )
              : intl.formatMessage({
                  id: 'EP_SLOGAN_EDIT_HINT',
                  defaultMessage: 'EP_SLOGAN_EDIT_HINT',
                })
          }
        />
      </div>
      <div className={styles.actionsWrapper}>
        <TextButton
          size={'medium'}
          disabled={loading}
          onClick={() =>
            executeWithDialog(
              () => pageContext?.goBackUrl && pageContext?.goBackUrl()
            )
          }
          data-cy="DISCARD_PAGE_INFO_BUTTON"
        >
          <FormattedMessage
            id="EP_DISCARD_CTA"
            defaultMessage="EP_DISCARD_CTA"
          />
        </TextButton>
        <Button
          className={styles.publishButton}
          loading={loading}
          size={'medium'}
          variant={'primary'}
          onClick={() => {
            trackingData &&
              trackEditSaving({
                focusType: pageContext.focusType,
                itemId: pageContext.pageId,
                module: trackingData.module,
                part: trackingData.part,
              });
            handleMutation();
          }}
          data-cy="PUBLISH_PAGE_INFO_BUTTON"
          data-testid="PUBLISH_PAGE_INFO_BUTTON"
        >
          <FormattedMessage
            id="EP_PUBLISH_CTA"
            defaultMessage="EP_PUBLISH_CTA"
          />
        </Button>
      </div>
      {dataChanged && dialogConfirmation?.dialogAction && <NotSavedDialog />}
    </>
  );
};

export default PageInfo;
